import React, { useEffect, useState } from 'react';
import { fallbackBuildInfo, formatAppVersion, loadBuildInfo } from '../appVersion';

function StepperSetting({ label, value, min, max, onChange }) {
    const decrement = () => onChange(Math.max(min, value - 1));
    const increment = () => onChange(Math.min(max, value + 1));

    return (
        <div className="stepper-setting">
            <span>{label}</span>
            <div className="stepper-control" aria-label={label}>
                <button
                    type="button"
                    aria-label={`Decrease ${label}`}
                    onClick={decrement}
                    disabled={value <= min}
                >
                    −
                </button>
                <output aria-live="polite">{value}</output>
                <button
                    type="button"
                    aria-label={`Increase ${label}`}
                    onClick={increment}
                    disabled={value >= max}
                >
                    +
                </button>
            </div>
        </div>
    );
}

export default function SettingsModal({ settings, onChange, onClose }) {
    const [localSettings, setLocalSettings] = useState(settings);
    const [buildInfo, setBuildInfo] = useState(fallbackBuildInfo);

    useEffect(() => {
        setLocalSettings(settings);
    }, [settings]);

    useEffect(() => {
        let isMounted = true;

        loadBuildInfo().then((loadedBuildInfo) => {
            if (isMounted) {
                setBuildInfo(loadedBuildInfo);
            }
        });

        return () => {
            isMounted = false;
        };
    }, []);

    function handleSave() {
        onChange(localSettings);
        onClose();
    }

    const minMaxScore = localSettings.targetScore + localSettings.winBy;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h2>Settings</h2>
                <form>
                    <label>
                        Red team name:
                        <input
                            type="text"
                            value={localSettings.redTeamName}
                            onChange={(e) => setLocalSettings({ ...localSettings, redTeamName: e.target.value })}
                        />
                    </label>
                    <label>
                        Blue team name:
                        <input
                            type="text"
                            value={localSettings.blueTeamName}
                            onChange={(e) => setLocalSettings({ ...localSettings, blueTeamName: e.target.value })}
                        />
                    </label>
                    <StepperSetting
                        label="Target score"
                        value={localSettings.targetScore}
                        min={1}
                        max={100}
                        onChange={(value) => setLocalSettings({
                            ...localSettings,
                            targetScore: value,
                            maxScore: Math.max(value + localSettings.winBy, localSettings.maxScore)
                        })}
                    />
                    <StepperSetting
                        label="Win by"
                        value={localSettings.winBy}
                        min={1}
                        max={20}
                        onChange={(value) => setLocalSettings({
                            ...localSettings,
                            winBy: value,
                            maxScore: Math.max(localSettings.targetScore + value, localSettings.maxScore)
                        })}
                    />
                    <StepperSetting
                        label="Max score"
                        value={localSettings.maxScore}
                        min={minMaxScore}
                        max={100}
                        onChange={(value) => setLocalSettings({
                            ...localSettings,
                            maxScore: value
                        })}
                    />
                    <label className="checkbox-setting">
                        <input
                            type="checkbox"
                            checked={localSettings.showScoreAnimations}
                            onChange={(e) => setLocalSettings({
                                ...localSettings,
                                showScoreAnimations: e.target.checked
                            })}
                        />
                        Show score animations
                    </label>
                </form>
                <p className="install-hint">
                    For a cleaner game view on your phone, add this app to your Home Screen and launch it from there.
                </p>
                <div className="modal-actions">
                    <button type="button" onClick={handleSave}>Done</button>
                    <button type="button" onClick={onClose}>Cancel</button>
                </div>
                <p className="app-version">Version {formatAppVersion(buildInfo)}</p>
            </div>
        </div>
    );
}
